import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { PrizeDto } from 'src/util/types';

import { useMemo, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack } from '@mui/material';
import axios from 'axios';
import config from 'src/config';
import { useAuth } from 'src/util/authenticationProvider';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import PrizeEdit from './PrizeEdit';

/* eslint-disable @typescript-eslint/no-unused-vars, no-unused-vars */
type PrizeListProps = {
  rows: PrizeDto[];
  deleteCallback: (id: number) => void;
  updateCallback: (updatedPrize: PrizeDto) => void;
  loading: boolean;
};
/* eslint-enable @typescript-eslint/no-unused-vars, no-unused-vars */

export default function PrizeList(props: PrizeListProps) {
  const [selectedRow, setSelectedRow] = useState<PrizeDto | null>(null);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const navigate=useNavigate();
  const authContext = useAuth();
  const {t} = useTranslation();
  const [deleteTarget, setDeleteTarget] = useState<PrizeDto | null>(null);

  const columns: GridColDef<PrizeDto>[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: t('prizes.name'),
        flex: 1,
        minWidth: 160
      },
      {
        field: 'description',
        headerName: t('prizes.description'),
        flex: 2,
        minWidth: 220
      },
      {
        field: 'image',
        headerName: t('prizes.image'),
        width: 180,
        renderCell: (params) => (
          <Box
            component="img"
            src={params.value}
            alt={params.row.name}
            sx={{ width: '100%', height: 110, objectFit: 'cover', borderRadius: 1 }}
          />
        )
      },
      {
        field: 'actions',
        headerName: t('general.Actions'),
        sortable: false,
        filterable: false,
        width: 200,
        renderCell: (params) => (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setSelectedRow(params.row);
                setOpenEdit(true);
              }}
            >
              {t('general.Edit')}
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              onClick={() => {
                setDeleteTarget(params.row);
                setOpenDialog(true);
              }}
            >
              {t('general.Delete')}
            </Button>
          </Stack>
        )
      }
    ],
    [t]
  );

  const deletePrize = () => {
    axios.delete(config.apiUrl + '/api/prize/delete/' + selectedRow.id, {
      headers: {
        'Authorization': 'Bearer ' + authContext.tokens.accessToken
      }
    })
      .then(res => {
        if (res.data.status >= 200) {
          setOpenDialog(false);
        }
        props.deleteCallback(deleteTarget.id);
        setDeleteTarget(null);
      })
      .catch(error=>{
        if (error.response?.status === 401) {
          authContext.logout();
          navigate('/cp/login');
        }
        alert('Der Prize wird bereits verwendet und kann nicht gelöscht werden');
      });
  };

  return (
    <Box sx={{ width: '100%', backgroundColor: '#fff' }}>
      <Dialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{t('prizes.editTitle')}</DialogTitle>
        <DialogContent dividers>
          {selectedRow && (
            <PrizeEdit
              {...selectedRow}
              onSuccess={(updatedPrize) => {
                props.updateCallback(updatedPrize);
                setSelectedRow(null);
                setOpenEdit(false);
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>{t('general.Close')}</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openDialog}
        onClose={() => { setOpenDialog(false); setDeleteTarget(null); }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('prizes.deleteTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t('prizes.deleteConfirm')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenDialog(false); setDeleteTarget(null); }}>{t('general.No')}</Button>
          <Button onClick={deletePrize} autoFocus>
            {t('general.Yes')}
          </Button>
        </DialogActions>
      </Dialog>
      
      <DataGrid
        rows={props.rows}
        rowHeight={160}
        columns={columns}
        getRowId={(row) => row.id}
        loading={props.loading}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
        }}
        pageSizeOptions={[10]}
        disableRowSelectionOnClick
      />
    </Box>
  );
}
